import { formatMoneyCompact, formatMoneyShort, formatMonth, formatMonthShort } from '@/lib/format';

export interface MonthBar {
  month: string;
  total: number;
  investmentCount: number;
}

/**
 * One series (planned spend per month), so no legend – the card title names it.
 * Only the tallest column is direct-labelled; the rest live in the hover title
 * and the axis, per the "label selectively" rule.
 */
export function MonthlyColumns({ data, currency }: { data: MonthBar[]; currency: string }) {
  const max = Math.max(...data.map((bar) => bar.total), 1);
  const peak = data.reduce((best, bar) => (bar.total > best.total ? bar : best), data[0]!);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-[2px] sm:gap-1" style={{ height: '11rem' }}>
        {data.map((bar) => {
          const height = Math.max((bar.total / max) * 100, bar.total > 0 ? 2 : 0);
          const isPeak = bar.month === peak.month;
          return (
            <div key={bar.month} className="group flex min-w-0 flex-1 flex-col justify-end gap-1">
              {isPeak ? (
                <span className="num truncate text-center text-[10px] font-medium text-[var(--ink-2)]">
                  {formatMoneyCompact(bar.total, currency)}
                </span>
              ) : null}
              <div
                className="mx-auto w-full max-w-6 rounded-t-[4px] transition-all duration-500 group-hover:brightness-110"
                style={{
                  height: `${height}%`,
                  background: isPeak ? 'var(--accent)' : 'color-mix(in oklch, var(--accent) 55%, var(--surface-3))',
                }}
                title={`${formatMonth(bar.month)}: ${formatMoneyShort(bar.total, currency)} · ${
                  bar.investmentCount
                } Investition(en)`}
              />
            </div>
          );
        })}
      </div>
      {/* Baseline + month ticks. */}
      <div className="border-t border-[var(--grid)]" />
      <div className="flex gap-[2px] sm:gap-1">
        {data.map((bar) => (
          <span
            key={bar.month}
            className="num min-w-0 flex-1 truncate text-center text-[10px] text-[var(--ink-3)]"
          >
            {formatMonthShort(bar.month)}
          </span>
        ))}
      </div>
    </div>
  );
}
